import React, { useState } from 'react'
import { X, Upload } from 'lucide-react'
import { maskAssetAPI } from '../../services/api'

const MaskAssetUploadDialog = ({ isOpen, onClose, onUploaded }) => {
  const [file, setFile] = useState(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [uploading, setUploading] = useState(false)

  if (!isOpen) {
    return null
  }

  const resetForm = () => {
    setFile(null)
    setName('')
    setDescription('')
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!file) {
      alert('Please select a mask frame/image/video file.')
      return
    }

    const formData = new FormData()
    formData.append('maskFile', file)
    if (name) formData.append('name', name)
    if (description) formData.append('description', description)

    setUploading(true)
    try {
      await maskAssetAPI.upload(formData)
      if (typeof onUploaded === 'function') {
        onUploaded()
      }
      resetForm()
      onClose()
    } catch (error) {
      console.error('Failed to upload mask asset:', error)
      alert(error?.response?.data?.detail || 'Failed to upload mask asset. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const getFileLabel = () => {
    if (!file) return 'PNG/SVG frame or MP4/WebM video'
    return file.name
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[1200] px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg animate-in fade-in zoom-in">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Add Mask Frame / Video</h3>
            <p className="text-sm text-gray-500">White areas reveal the actual content, transparent areas stay clear.</p>
          </div>
          <button
            onClick={() => {
              resetForm()
              onClose()
            }}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mask File
            </label>
            <label className="flex items-center justify-between w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 transition-colors">
              <div>
                <p className="text-sm text-gray-700 font-medium">{getFileLabel()}</p>
                <p className="text-xs text-gray-500">Supported: PNG, SVG, transparent video (MP4/WebM/Mov)</p>
              </div>
              <Upload className="w-5 h-5 text-gray-400" />
              <input
                type="file"
                className="hidden"
                accept=".png,.jpg,.jpeg,.webp,.svg,video/mp4,video/webm,video/quicktime,video/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Optional friendly name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Notes about how to use this mask frame (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                resetForm()
                onClose()
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !file}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload Mask Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MaskAssetUploadDialog

